/**
 * Pure geometry for the dope sheet: frame↔pixel mapping, row/ruler bands, tick density,
 * hit testing, rubber-band selection and magnet snapping.
 *
 * Nothing here touches the DOM, the canvas or the store — the sheet passes in the numbers it
 * has, so every rule is unit-testable without a browser.
 *
 * Coordinate system: CSS pixels relative to the canvas' top-left corner.
 *
 *   x:  [0, laneWidth)                 bone-name column
 *       [laneWidth, width)             frame area
 *   y:  [0, rulerHeight)               frame ruler          (row -1)
 *       [rulerHeight, rowsTop)         animation-event band (row -1)
 *       first row after rowsTop        "All" summary row    (row 0)
 *       following rows                 one per visible bone (row = boneIndex + 1)
 */

import type { CanonicalBoneId, KeyframeSelection } from '../../shared';

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

export const LANE_WIDTH = 116;
export const RULER_HEIGHT = 18;
export const ROW_HEIGHT = 16;
/** Pixels per frame at `zoom === 1`. Zoom is clamped to [0.1, 32] by the store. */
export const BASE_PIXELS_PER_FRAME = 6;
export const KEY_HIT_RADIUS = 6;
export const MAGNET_PIXELS = 8;
/**
 * The animation-event strip, drawn between the frame ruler and the rows.
 *
 * A band rather than an extra row: events belong to the CLIP, not to a bone, and putting them in
 * the row stack would have made them look like one more bone track.
 */
export const EVENT_BAND_HEIGHT = 14;
export const EVENT_HIT_RADIUS = 7;
/** Canvas width used when the element has not been laid out yet (jsdom, first frame). */
export const FALLBACK_WIDTH = 800;

export interface TimelineMetrics {
  /** Canvas width in CSS pixels. */
  width: number;
  /** Canvas height in CSS pixels. */
  height: number;
  laneWidth: number;
  rulerHeight: number;
  /** Height of the animation-event strip below the ruler. */
  eventBandHeight: number;
  rowHeight: number;
  zoom: number;
  scrollFrame: number;
}

export function createMetrics(patch: Partial<TimelineMetrics> = {}): TimelineMetrics {
  return {
    width: FALLBACK_WIDTH,
    height: RULER_HEIGHT + EVENT_BAND_HEIGHT + ROW_HEIGHT,
    laneWidth: LANE_WIDTH,
    rulerHeight: RULER_HEIGHT,
    // Defaulted to the REAL value, not 0. A 0 default would keep every geometry assertion in
    // layout.test.ts green while testing a sheet the app never ships.
    eventBandHeight: EVENT_BAND_HEIGHT,
    rowHeight: ROW_HEIGHT,
    zoom: 1,
    scrollFrame: 0,
    ...patch,
  };
}

export function pixelsPerFrame(zoom: number): number {
  return BASE_PIXELS_PER_FRAME * (Number.isFinite(zoom) && zoom > 0 ? zoom : 1);
}

export function frameToX(frame: number, m: TimelineMetrics): number {
  return m.laneWidth + (frame - m.scrollFrame) * pixelsPerFrame(m.zoom);
}

/** Fractional frame under `x`. Callers round before writing anything into the clip. */
export function xToFrame(x: number, m: TimelineMetrics): number {
  return m.scrollFrame + (x - m.laneWidth) / pixelsPerFrame(m.zoom);
}

/**
 * Where the rows start — below the ruler AND below the event band.
 *
 * Every vertical calculation goes through this, because the sheet now has THREE bands above the
 * rows, not two. Four call sites used to compare `y` against `rulerHeight` directly and each of
 * them meant "above the rows"; a click on an event marker would otherwise scrub the playhead, and
 * a double-click on the band would fall through and insert a keyframe on whatever row the
 * arithmetic happened to produce.
 */
export function rowsTop(m: TimelineMetrics): number {
  return m.rulerHeight + m.eventBandHeight;
}

/** Total canvas height needed for `rowCount` bone rows (plus ruler, event band and summary row). */
export function sheetHeight(rowCount: number, m: TimelineMetrics): number {
  return rowsTop(m) + (Math.max(0, rowCount) + 1) * m.rowHeight;
}

/** Top edge of a row; row 0 is the summary row, row `i + 1` is bone `i`. */
export function rowTop(row: number, m: TimelineMetrics): number {
  return rowsTop(m) + row * m.rowHeight;
}

export function rowCenterY(row: number, m: TimelineMetrics): number {
  return rowTop(row, m) + m.rowHeight / 2;
}

/** Row under `y`: -1 on the ruler or event band, 0 on the summary row, `boneIndex + 1` on a bone. */
export function rowAtY(y: number, m: TimelineMetrics): number {
  if (y < rowsTop(m)) return -1;
  return Math.floor((y - rowsTop(m)) / m.rowHeight);
}

/** Bone index under `y`, or -1 when the pointer is above the rows or on the summary row. */
export function boneIndexAtY(y: number, m: TimelineMetrics): number {
  const row = rowAtY(y, m);
  return row >= 1 ? row - 1 : -1;
}

/** True when `y` is inside the event band — between the ruler and the rows. */
export function isOnEventBand(y: number, m: TimelineMetrics): boolean {
  return m.eventBandHeight > 0 && y >= m.rulerHeight && y < rowsTop(m);
}

export function eventBandCenterY(m: TimelineMetrics): number {
  return m.rulerHeight + m.eventBandHeight / 2;
}

/**
 * Index of the event nearest `x`, or -1 when none is within `EVENT_HIT_RADIUS`.
 *
 * Ties go to the LAST match so the marker drawn on top — the one the user can actually see — is
 * the one that gets picked when several share a frame.
 */
export function eventIndexAtX(
  x: number,
  events: readonly { frame: number }[],
  m: TimelineMetrics,
): number {
  let best = -1;
  let bestDistance = EVENT_HIT_RADIUS;
  for (let i = 0; i < events.length; i++) {
    const distance = Math.abs(frameToX(events[i].frame, m) - x);
    if (distance <= bestDistance) {
      bestDistance = distance;
      best = i;
    }
  }
  return best;
}

const TICK_STEPS: readonly number[] = [1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000];

/**
 * Frames between labelled ruler ticks: the smallest step from a 1-2-5 ladder that keeps labels
 * at least `minLabelPx` apart. Non-increasing as zoom grows.
 */
export function tickSpacing(zoom: number, minLabelPx = 48): number {
  const ppf = pixelsPerFrame(zoom);
  for (const step of TICK_STEPS) {
    if (step * ppf >= minLabelPx) return step;
  }
  return TICK_STEPS[TICK_STEPS.length - 1];
}

/** Inclusive integer frame range currently visible in the frame area. */
export function visibleFrameRange(
  m: TimelineMetrics,
  durationFrames: number,
): { first: number; last: number } {
  const first = Math.max(0, Math.floor(xToFrame(m.laneWidth, m)));
  const last = Math.min(
    Math.max(0, durationFrames),
    Math.ceil(xToFrame(Math.max(m.laneWidth, m.width), m)),
  );
  return { first, last: Math.max(first, last) };
}

// ---------------------------------------------------------------------------
// Rows, hit testing, box selection
// ---------------------------------------------------------------------------

/** One dope-sheet lane: a bone and the frames it has keys on (sorted ascending). */
export interface SheetRow {
  bone: CanonicalBoneId;
  frames: readonly number[];
}

export interface KeyHit {
  /** 0 = summary row, `boneIndex + 1` = bone row. */
  row: number;
  /** `null` for a hit on the summary row. */
  bone: CanonicalBoneId | null;
  frame: number;
  distance: number;
}

export interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export function normalizeRect(x0: number, y0: number, x1: number, y1: number): Rect {
  return {
    x0: Math.min(x0, x1),
    y0: Math.min(y0, y1),
    x1: Math.max(x0, x1),
    y1: Math.max(y0, y1),
  };
}

export function rectSize(rect: Rect): number {
  return Math.max(rect.x1 - rect.x0, rect.y1 - rect.y0);
}

/**
 * Nearest keyframe diamond to (`x`, `y`) within `radius` CSS pixels, or `null`.
 * Summary-row diamonds (`summaryFrames`) participate and come back with `bone: null`.
 */
export function hitTestKeys(
  x: number,
  y: number,
  rows: readonly SheetRow[],
  summaryFrames: readonly number[],
  m: TimelineMetrics,
  radius: number = KEY_HIT_RADIUS,
): KeyHit | null {
  let best: KeyHit | null = null;

  const consider = (row: number, bone: CanonicalBoneId | null, frame: number): void => {
    const dx = frameToX(frame, m) - x;
    const dy = rowCenterY(row, m) - y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance > radius) return;
    if (best && best.distance <= distance) return;
    best = { row, bone, frame, distance };
  };

  for (const frame of summaryFrames) consider(0, null, frame);
  rows.forEach((row, index) => {
    for (const frame of row.frames) consider(index + 1, row.bone, frame);
  });

  return best;
}

/** Every bone-row keyframe whose diamond centre falls inside `rect`. */
export function boxSelectKeys(
  rect: Rect,
  rows: readonly SheetRow[],
  m: TimelineMetrics,
): KeyframeSelection {
  const out: KeyframeSelection = [];
  rows.forEach((row, index) => {
    const y = rowCenterY(index + 1, m);
    if (y < rect.y0 || y > rect.y1) return;
    for (const frame of row.frames) {
      const x = frameToX(frame, m);
      if (x < rect.x0 || x > rect.x1) continue;
      out.push({ bone: row.bone, frame });
    }
  });
  return out;
}

// ---------------------------------------------------------------------------
// Snapping
// ---------------------------------------------------------------------------

/** Magnet radius in frames for the current zoom (a fixed pixel distance on screen). */
export function magnetThresholdFrames(zoom: number, px: number = MAGNET_PIXELS): number {
  return px / pixelsPerFrame(zoom);
}

export interface KeyDragInput {
  /** Frame of the diamond that was grabbed. */
  anchorFrame: number;
  /** Fractional frame under the pointer when the drag started. */
  startFrame: number;
  /** Fractional frame under the pointer now. */
  pointerFrame: number;
  /** Frames the drag may snap to (unselected keys, the playhead). */
  candidates: readonly number[];
  snap: boolean;
  zoom: number;
  /** Whole frames already applied by this drag. */
  applied: number;
}

/**
 * Whole-frame step a key drag should apply right now (0 = nothing changed yet). Frames in a clip
 * are always integers, so the result is always rounded; `snap` adds the magnet to nearby frames.
 */
export function keyDragStep(input: KeyDragInput): number {
  const raw = input.anchorFrame + (input.pointerFrame - input.startFrame);
  const target = input.snap
    ? magnetSnapFrame(raw, input.candidates, magnetThresholdFrames(input.zoom))
    : raw;
  return Math.round(target) - input.anchorFrame - input.applied;
}

/**
 * Pull `frame` to the closest candidate within `threshold` frames; otherwise leave it alone.
 * Ties go to the smaller candidate frame, so the result is deterministic.
 */
export function magnetSnapFrame(
  frame: number,
  candidates: readonly number[],
  threshold: number,
): number {
  if (threshold <= 0) return frame;
  let best: number | null = null;
  let bestDistance = Infinity;
  for (const candidate of candidates) {
    const distance = Math.abs(candidate - frame);
    if (distance > threshold) continue;
    if (distance < bestDistance || (distance === bestDistance && best !== null && candidate < best)) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best ?? frame;
}
